/**
 * API-Client für `/api/web` und `/api/admin` (Cookie-Session, same-origin).
 * Fehler kommen immer im Format `{ error: { code, message, details }, requestId }` (architecture.md 7.1).
 */

export class ApiError extends Error {
  override name = 'ApiError';
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly requestId: string | null = null,
  ) {
    super(message);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type Query = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
  requestId?: string;
}

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Server nicht erreichbar. Bitte Verbindung prüfen.');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // kein JSON, z. B. eine Fehlerseite des Proxys
  }
  if (!res.ok) {
    const err = (json as ErrorBody | null) ?? {};
    throw new ApiError(
      res.status,
      err.error?.code ?? `HTTP_${res.status}`,
      err.error?.message ?? `Unerwartete Antwort (${res.status}).`,
      err.error?.details ?? {},
      err.requestId ?? res.headers.get('x-request-id'),
    );
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', withQuery(path, query)),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T = void>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}
